import { Button } from '@roger/ui-components';

import type {
  EpisodeTakeOverUndone,
  PatientResponse,
} from '../../services/api/types.gen';
import { formatDateForDisplay, formatDateToISO } from '../../utils/dateUtils';
import {
  CHART_MOVED_EXTERNAL_BILLS_LINE,
  UNDO_BUTTON,
  chartMovedLine,
  takeOverUndoneLine,
} from './takeOverCopy';

/** An instant, as the day it fell on where the reader is ("Oct 13, 2026"). */
function formatMoment(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return formatDateForDisplay(formatDateToISO(date));
}

export interface ChartMovedNoticeProps {
  movedAt: string;
  /** The day Roger charts this chart from, when Take Over set it. */
  chartsFrom?: string | null;
  /**
   * How the chart moved. Take Over is the default. Add Episode and an intake
   * leave the imported period's billing with the External EMR, so the
   * notice says so and offers no Undo.
   */
  via?: PatientResponse['chart_moved_via'];
  /**
   * Opens Undo Take Over. Pass it only to holders of `episode:take-over`,
   * the same gate as the Take Over button; without it no Undo shows.
   */
  onUndo?: () => void;
}

/**
 * "This chart moved to Roger on {date}. Chart all new work in Roger." When
 * Roger charts it from another day: "Roger charts it from {day}." instead.
 */
export function ChartMovedNotice({
  movedAt,
  chartsFrom,
  via,
  onUndo,
}: ChartMovedNoticeProps) {
  const byTakeOver = !via || via === 'take_over';
  const movedOn = formatMoment(movedAt);
  const chartsFromDay = chartsFrom ? formatDateForDisplay(chartsFrom) : '';
  return (
    <div
      className='flex flex-wrap items-center gap-2 border-b bg-muted/30 px-4 py-2 text-sm md:px-6'
      role='status'
      data-testid='chart-moved-notice'
    >
      <span>{chartMovedLine(movedOn, chartsFromDay)}</span>
      {!byTakeOver && <span>{CHART_MOVED_EXTERNAL_BILLS_LINE}</span>}
      {byTakeOver && onUndo && (
        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={onUndo}
          data-testid='chart-moved-undo'
        >
          {UNDO_BUTTON}
        </Button>
      )}
    </div>
  );
}

/** "Take Over undone {date} by {name}: {reason}", on the episode header. */
export function TakeOverUndoneTrace({
  undone,
}: {
  undone: EpisodeTakeOverUndone;
}) {
  const date = formatMoment(undone.at);
  return (
    <span className='text-xs' data-testid='take-over-undone-trace'>
      {takeOverUndoneLine(date, undone.by_user_name, undone.reason)}
    </span>
  );
}
